// PWA — 提交表单的纯展示 / 校验逻辑（可测，Req 7.1/7.2/7.3/7.4/6.4，任务 9.4）
//
// 把「表单字段 + 电源状态」映射为可提交性判定、尺寸规范化提示、以及最终发往
// `POST /jobs` 的 GenerationRequest。纯函数、无副作用，便于单元测试；
// 组件与 hook 复用之。
//
// 复用 shared：
//  - normalizeTo16（任务 2.1）：尺寸规范化为最接近的 16 倍数（Req 7.3, 7.4）。
//  - canSubmit（submitGate，任务 2.21）：仅 running 允许提交，否则「请先启动实例」（Req 6.4）。
//  - DEFAULT_DIMS：合法默认尺寸 1216×832（Req 7.3）。

import {
  normalizeTo16,
  canSubmit,
  type PowerState,
  type GenerationRequest,
} from "@comfy/shared";

/** 生成参数缺省值（design.md「提交任务」一节，与 submit Lambda 保持一致）。 */
export const DEFAULT_STEPS = 20;
export const DEFAULT_GUIDANCE = 3.5;

/** seed 取值模式：随机（提交时序列化为 "random"）或固定数值。 */
export type SeedMode = "random" | "fixed";

/** 提交表单的内部值模型（受控组件状态）。 */
export interface SubmitFormValues {
  positivePrompt: string;
  negativePrompt: string;
  /** 宽（提交前经 normalizeTo16 规范化）。 */
  width: number;
  /** 高（提交前经 normalizeTo16 规范化）。 */
  height: number;
  steps: number;
  guidance: number;
  seedMode: SeedMode;
  /** 固定模式下的 seed 数值（随机模式忽略）。 */
  seed: number;
}

/**
 * 表单默认尺寸：微信公众号封面尺寸。
 * 公众号封面推荐 1200×675（16:9），但 FLUX 要求宽高均为 16 的倍数，675 非 16 倍数；
 * 故默认取最贴近的合规尺寸 1216×688（均为 16 倍数、约 16:9），出图后裁到 1200×675。
 * 仅作为表单缺省值，不改动 shared 的 DEFAULT_DIMS（后端回退与其他逻辑仍用之）。
 */
export const WECHAT_COVER_DIMS = { width: 1216, height: 688 } as const;

/** 表单默认值：公众号封面默认尺寸 + 默认 steps/guidance + 随机 seed。 */
export function defaultFormValues(): SubmitFormValues {
  return {
    positivePrompt: "",
    negativePrompt: "",
    width: WECHAT_COVER_DIMS.width,
    height: WECHAT_COVER_DIMS.height,
    steps: DEFAULT_STEPS,
    guidance: DEFAULT_GUIDANCE,
    seedMode: "random",
    seed: 0,
  };
}

/** 单个尺寸字段的规范化结果。 */
export interface DimNormalization {
  /** 规范化后的合法尺寸（16 的倍数、≥16）。 */
  value: number;
  /** 输入是否被调整（原值非合法 16 倍数）。 */
  adjusted: boolean;
  /** 当被调整时面向用户的提示；未调整为 null（Req 7.4）。 */
  hint: string | null;
}

/**
 * 规范化单个尺寸输入并生成提示（Req 7.3, 7.4）。
 *
 * 复用 shared 的 {@link normalizeTo16}：任意数值 → 最接近的 16 倍数且 ≥16。
 * 当规范化结果与（四舍五入后的）原输入不同，则视为「已调整」并给出提示。
 *
 * @param raw 用户输入的原始数值（可能非整数 / 非 16 倍数 / 过小）。
 */
export function normalizeDimension(raw: number): DimNormalization {
  // 非有限值（NaN / Infinity）回落到最小合法尺寸 16，避免把 NaN 透传给
  // normalizeTo16（该函数对 NaN 返回 NaN）；并强制视为「已调整」。
  const finite = Number.isFinite(raw) ? raw : 16;
  const rounded = Math.round(finite);
  const value = normalizeTo16(finite);
  const adjusted = !Number.isFinite(raw) || rounded !== value;
  const hint = adjusted
    ? `已调整为 ${value}（最接近的 16 倍数）`
    : null;
  return { value, adjusted, hint };
}

/** 可提交性判定结果。 */
export interface SubmitAvailability {
  /** 是否允许提交。 */
  canSubmit: boolean;
  /** 不可提交时的原因（用于按钮禁用提示）；可提交时为 null。 */
  reason: string | null;
}

/**
 * 综合电源状态与提示词是否填写，判定能否提交（Req 6.4, 7.1）。
 *
 * - 电源门控优先：复用 shared 的 {@link canSubmit}，非 running 一律拒绝并提示
 *   「请先启动实例」。
 * - 通过门控后，提示词为空亦不可提交。
 *
 * @param state 当前 {@link PowerState}。
 * @param promptFilled 提示词是否已填写（去空白后非空）。
 */
export function deriveSubmitAvailability(
  state: PowerState,
  promptFilled: boolean,
): SubmitAvailability {
  const gate = canSubmit(state);
  if (!gate.allowed) {
    return { canSubmit: false, reason: gate.message ?? "请先启动实例" };
  }
  if (!promptFilled) {
    return { canSubmit: false, reason: "请输入提示词" };
  }
  return { canSubmit: true, reason: null };
}

/**
 * 依据表单值构造最终发往 `POST /jobs` 的 {@link GenerationRequest}。
 *
 * - 提示词去除首尾空白；
 * - 宽 / 高经 {@link normalizeTo16} 规范化（防御性，即使 UI 已规范化）；
 * - steps 取整且 ≥1；guidance 原样透传；
 * - seed：随机模式 → 字面量 "random"，固定模式 → 取整数值。
 */
export function buildRequest(values: SubmitFormValues): GenerationRequest {
  return {
    positivePrompt: values.positivePrompt.trim(),
    negativePrompt: values.negativePrompt.trim(),
    width: normalizeTo16(values.width),
    height: normalizeTo16(values.height),
    steps: Math.max(1, Math.round(values.steps)),
    guidance: values.guidance,
    seed: values.seedMode === "random" ? "random" : Math.round(values.seed),
  };
}
